import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  message,
  Spin,
  Space,
  Typography,
  Row,
  Col,
  Divider,
} from "antd";
import {
  PlusOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import api from "../../../services/api";
import CommonSelect from "../../../core/common/commonSelect";
import { showBasicAlert } from "../../../core/common/useAlert";
// import CommonSelect from "../../../core/common/CommonSelect"; // Adjust import path as needed

const { Text, Title } = Typography;
const { TextArea } = Input;

interface LeadForm {
  key: number;
  LeadFormName: string;
  CompanyName: string;
  CompanyId: number;
  CreatedDate: string;
  Image: string;
  CompanyType: string;
  Status: string;
  structure: FormField[];
  Pipeline: string;
  employees: number[];
  pipeline_id: number;
  deal_pipeline_id: number | null;
}

interface FormField {
  id: number;
  type: string;
  label: string;
  required: boolean;
  typeCheck: string;
  options?: string[];
}

interface Conversation {
  id: number;
  customer_name: string;
  phone: string;
  last_message: string;
  last_message_type: string;
  last_message_time: string;
  total_messages: number;
  unread_count: number;
  company_id: number;
  company_type: string;
  messages: any[];
  lead_status: string;
  lead_score: number;
  waba_id: number;
  phone_number_id: string;
}

interface AddLeadFromFormModalProps {
  visible: boolean;
  onCancel: () => void;
  conversation: Conversation | null;
  signature: string | null;
  onSuccess?: () => void;
}

const AddLeadFromWhatsAppFormModal: React.FC<AddLeadFromFormModalProps> = ({
  visible,
  onCancel,
  conversation,
  signature,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [leadForms, setLeadForms] = useState<LeadForm[]>([]);
  const [selectedForm, setSelectedForm] = useState<LeadForm | null>(null);
  const [formOptions, setFormOptions] = useState<any[]>([]);
  const [priorityOption, setPriorityOption] = useState({
    value: "medium",
    label: "Medium",
  });
  // Fetch lead forms and format options for CommonSelect
  const fetchLeadForms = async () => {
    if (!conversation || !signature) return;

    try {
      setLoading(true);
      const response = await api.post("leadService/getLeadFormsByCompany", {
        signature,
        company_id: conversation.company_id,
        company_type: conversation.company_type,
      });

      if (response.data?.msg === "Data Found" && response.data?.data?.data) {
        const forms = response.data.data.data;
        setLeadForms(forms);

        // Format options for CommonSelect
        const options = forms.map((form: LeadForm) => ({
          value: form.key.toString(),
          label: form.LeadFormName,
          pipeline: form.Pipeline,
          formData: form, // Store the full form data
        }));
        setFormOptions(options);

        // Auto-fill form with conversation data
        if (conversation) {
          form.setFieldsValue({
            "Mobile No": conversation.phone.replace(/\D/g, ""),
            "Full Name": conversation.customer_name,
          });
        }
      }
    } catch (err: any) {
      console.error("Error fetching lead forms:", err);
      message.error(
        err?.response?.data?.message?.[0]?.msg || "Error fetching forms"
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle form selection for CommonSelect
  const handleFormSelect = (selectedOption: any) => {
    if (selectedOption) {
      const form = leadForms.find(
        (f) => f.key.toString() === selectedOption.value
      );
      setSelectedForm(form || null);
    } else {
      setSelectedForm(null);
    }
  };

  // Custom format for CommonSelect options
  const formatOptionLabel = (option: any) => {
    return (
      <div style={{ padding: "4px 0" }}>
        <div style={{ fontWeight: 500, fontSize: "14px" }}>{option.label}</div>
        <div style={{ fontSize: "12px", color: "#666" }}>
          Pipeline: {option.pipeline}
        </div>
      </div>
    );
  };

  // Get icon for field type
  const getFieldIcon = (type: string) => {
    switch (type) {
      case "text":
        return <UserOutlined />;
      case "tel":
        return <PhoneOutlined />;
      case "email":
        return <MailOutlined />;
      case "country":
        return <GlobalOutlined />;
      default:
        return <UserOutlined />;
    }
  };

  // Get input type
  const getInputType = (type: string) => {
    switch (type) {
      case "text":
        return "text";
      case "tel":
        return "tel";
      case "email":
        return "email";
      case "number":
        return "number";
      case "textarea":
        return "textarea";
      default:
        return "text";
    }
  };

  // Render form fields
  const renderFormFields = () => {
    if (!selectedForm) return null;

    return selectedForm.structure.map((field) => (
      <Form.Item
        key={field.id}
        name={field.label}
        label={field.label}
        rules={[
          { required: field.required, message: `Please enter ${field.label}` },
        ]}
      >
        {field.type === "select" ? (
          <CommonSelect
            options={
              field.options?.map((opt) => ({
                value: opt,
                label: opt,
              })) || []
            }
            placeHolder={`Select ${field.label}`}
          />
        ) : field.type === "textarea" ? (
          <TextArea rows={3} placeholder={`Enter ${field.label}`} />
        ) : (
          <Input
            type={getInputType(field.type)}
            placeholder={`Enter ${field.label}`}
            prefix={getFieldIcon(field.type)}
          />
        )}
      </Form.Item>
    ));
  };

  // Handle submit
  const handleSubmit = async (values: any) => {
    if (!selectedForm || !conversation || !signature) return;

    try {
      setSubmitting(true);
      const { form_id, lead_priority, ...cleanResponse } = values;

      const sourceDetails = {
        source_type: "WHATSAPP",
        whatsapp_phone_number_id: conversation.phone_number_id || null,
        whatsapp_display_phone_number: null,
        whatsapp_business_name: null,
        whatsapp_business_account_id: null,
        whatsapp_waba_id: conversation.waba_id || null,
        customer_phone_number: conversation.phone,
        customer_name: conversation.customer_name || null,
        message_type: "manual_chat_form",
        received_at: new Date().toISOString(),
      };

      // Prepare payload for the new API
      const payload = {
        signature,
        form_id: selectedForm.key,
        response: cleanResponse,
        company_id: conversation.company_id,
        company_type: conversation.company_type,
        lead_source: "Social Media",
        lead_source_child: "WhatsApp",
        priority: values.lead_priority || "Medium",
        source_details: sourceDetails,
      };

      const response = await api.post(
        "leadService/addWhatsAppLeadResponse",
        payload
      );

      if (response.data?.message?.[0]?.status === true) {
        showBasicAlert("Lead added successfully!");

        // Trigger any success callback
        if (onSuccess) {
          onSuccess();
        }

        // Reset and close
        form.resetFields();
        setSelectedForm(null);
        onCancel();
      }
    } catch (err: any) {
      showBasicAlert(err?.data?.message[0]?.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Initialize form when conversation changes
  useEffect(() => {
    if (conversation && visible) {
      fetchLeadForms();
    }
  }, [conversation, visible]);

  return (
    <Modal
      title={
        <div>
          <Title level={4} style={{ margin: 0 }}>
            Add Lead from Form
          </Title>
          {conversation && (
            <Text type="secondary" style={{ fontSize: "14px", marginTop: 4 }}>
              {conversation.customer_name} • {conversation.phone}
            </Text>
          )}
        </div>
      }
      open={visible}
      onCancel={() => {
        form.resetFields();
        setSelectedForm(null);
        onCancel();
      }}
      width={800}
      footer={null}
      destroyOnClose
    >
      <Spin spinning={loading}>
        {/* Form Selection */}
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            lead_priority: "medium",
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="form_id"
                label="Select Form"
                rules={[{ required: true, message: "Please select a form" }]}
              >
                <CommonSelect
                  options={formOptions}
                  onChange={handleFormSelect}
                  placeHolder="Select a lead form"
                  isDisabled={loading}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              {selectedForm && (
                <div
                  style={{
                    padding: "16px",
                    background: "#f0f2f5",
                    borderRadius: "8px",
                  }}
                >
                  <div
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <div>
                      <Text strong>Selected Form:</Text>
                      <div>{selectedForm.LeadFormName}</div>
                    </div>
                    <div>
                      <Text strong>Pipeline:</Text>
                      <div>{selectedForm.Pipeline}</div>
                    </div>
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary">
                      {selectedForm.structure.length} fields • Created:{" "}
                      {selectedForm.CreatedDate}
                    </Text>
                  </div>
                </div>
              )}
            </Col>
          </Row>

          {selectedForm && (
            <>
              <Divider>Fill Form Details</Divider>

              <div
                style={{
                  maxHeight: "400px",
                  overflowY: "auto",
                  padding: "0 8px",
                }}
              >
                {renderFormFields()}
              </div>

              <Divider />

              {/* Additional Fields */}
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="lead_priority" label="Lead Priority">
                    <CommonSelect
                      options={[
                        { value: "high", label: "High" },
                        { value: "medium", label: "Medium" },
                        { value: "low", label: "Low" },
                      ]}
                      placeHolder="Select priority"
                      defaultValue={priorityOption}
                      onChange={(option: any) => {
                        setPriorityOption(option);
                        form.setFieldsValue({
                          lead_priority: option?.value,
                        });
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Submit Button */}
              <div style={{ textAlign: "right", marginTop: 24 }}>
                <Space>
                  <Button
                    onClick={() => {
                      form.resetFields();
                      setSelectedForm(null);
                      onCancel();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={submitting}
                    icon={<PlusOutlined />}
                  >
                    Add Lead
                  </Button>
                </Space>
              </div>
            </>
          )}

          {!selectedForm && !loading && (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <div
                style={{ fontSize: "48px", color: "#d9d9d9", marginBottom: 16 }}
              >
                <PlusOutlined />
              </div>
              <Title level={4} style={{ color: "#999" }}>
                Select a Form
              </Title>
              <Text type="secondary">
                Choose a lead form from the dropdown above to start adding this
                conversation as a lead
              </Text>
            </div>
          )}
        </Form>
      </Spin>
    </Modal>
  );
};

export default AddLeadFromWhatsAppFormModal;
